// Polyline encoding/decoding (Google encoded polyline algorithm, precision 5 or 6)
export type LngLat = [number, number]; // [lng, lat]

export function decodePolyline(encoded: string, precision = 5): LngLat[] {
  const factor = Math.pow(10, precision);
  let index = 0,
    lat = 0,
    lng = 0;
  const coordinates: LngLat[] = [];
  while (index < encoded.length) {
    let result = 1,
      shift = 0,
      b: number;
    do {
      b = encoded.charCodeAt(index++) - 63 - 1;
      result += b << shift;
      shift += 5;
    } while (b >= 0x1f);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    result = 1;
    shift = 0;
    do {
      b = encoded.charCodeAt(index++) - 63 - 1;
      result += b << shift;
      shift += 5;
    } while (b >= 0x1f);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    coordinates.push([lng / factor, lat / factor]);
  }
  return coordinates;
}

// Haversine distance in meters
export function haversine(a: LngLat, b: LngLat): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

export function totalDistance(coords: LngLat[]): number {
  let d = 0;
  for (let i = 1; i < coords.length; i++) d += haversine(coords[i - 1], coords[i]);
  return d;
}

/**
 * Sample a polyline down to at most `maxPoints` evenly-spaced points
 * (always includes start + end). Use this for API-bound sampling so
 * the number of downstream calls stays bounded regardless of route length.
 */
export function samplePolylineMax(coords: LngLat[], maxPoints: number): LngLat[] {
  if (coords.length === 0) return [];
  if (maxPoints <= 1) return [coords[0]];
  if (coords.length <= maxPoints) return coords.slice();
  const out: LngLat[] = [];
  const step = (coords.length - 1) / (maxPoints - 1);
  for (let i = 0; i < maxPoints; i++) {
    out.push(coords[Math.round(i * step)]);
  }
  return out;
}

// Sample a polyline approximately every `intervalMeters`
export function samplePolyline(coords: LngLat[], intervalMeters = 250): LngLat[] {
  if (coords.length === 0) return [];
  const out: LngLat[] = [coords[0]];
  let acc = 0;
  for (let i = 1; i < coords.length; i++) {
    const seg = haversine(coords[i - 1], coords[i]);
    acc += seg;
    if (acc >= intervalMeters) {
      out.push(coords[i]);
      acc = 0;
    }
  }
  if (out[out.length - 1] !== coords[coords.length - 1]) out.push(coords[coords.length - 1]);
  return out;
}

/**
 * Min/max of a numeric array using a single linear pass.
 * Avoids `Math.min(...arr)` / `Math.max(...arr)`, which throw a
 * RangeError on long arrays (route polylines can have 5–10k points).
 */
export function minMax(arr: ArrayLike<number>): { min: number; max: number } {
  let min = Infinity, max = -Infinity;
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i];
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return { min, max };
}

// Bounding box from coords with margin in degrees
export function bbox(coords: LngLat[], pad = 0.01) {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  }
  return { minLng: minLng - pad, minLat: minLat - pad, maxLng: maxLng + pad, maxLat: maxLat + pad };
}

// Distance from point to nearest polyline vertex (cheap proxy for buffer test)
export function nearestDistance(point: LngLat, polyline: LngLat[]): number {
  let best = Infinity;
  for (const v of polyline) {
    const d = haversine(point, v);
    if (d < best) best = d;
  }
  return best;
}

// Cumulative distance from start along the polyline (length === coords.length)
export function cumulativeDistances(coords: LngLat[]): number[] {
  const out: number[] = new Array(coords.length).fill(0);
  for (let i = 1; i < coords.length; i++) {
    out[i] = out[i - 1] + haversine(coords[i - 1], coords[i]);
  }
  return out;
}

/**
 * Project a point onto the closest segment of a polyline.
 * Returns the snapped LngLat, the distance from the point to the line (m),
 * the segment index, and the cumulative distance along the line up to that snap.
 */
export function nearestPointOnPolyline(
  point: LngLat,
  coords: LngLat[],
  cum?: number[],
  /**
   * Optional map-matching hint: the route distance (`along`) where the
   * point was last known to be. When provided, candidate snap segments are
   * tie-broken toward this value — so a route that passes near itself (a
   * winding scenic detour, a hairpin, two parallel streets a block apart)
   * can't teleport the snapped position to a far-away part of the route
   * and hand navigation a maneuver from the wrong place. A genuinely large
   * jump (a real reroute, a GPS leap) still wins on its own, because then
   * the near-hint segments are far in cross-distance and lose anyway.
   */
  hintAlong?: number,
): { snapped: LngLat; distance: number; segmentIndex: number; along: number } {
  if (coords.length === 0) {
    return { snapped: point, distance: Infinity, segmentIndex: 0, along: 0 };
  }
  if (coords.length === 1) {
    return { snapped: coords[0], distance: haversine(point, coords[0]), segmentIndex: 0, along: 0 };
  }
  const cums = cum ?? cumulativeDistances(coords);
  // Local equirectangular projection around the point for cheap projection math.
  const lat0 = (point[1] * Math.PI) / 180;
  const mPerDegLat = 111320;
  const mPerDegLng = 111320 * Math.cos(lat0);
  const toXY = (p: LngLat): [number, number] => [
    (p[0] - point[0]) * mPerDegLng,
    (p[1] - point[1]) * mPerDegLat,
  ];
  // Meters of "score" added per meter of gap between a candidate snap and
  // the hint. Small enough that normal forward progress is unaffected,
  // large enough that a self-crossing hundreds of meters away loses to the
  // segment the driver is actually on.
  const ALONG_PENALTY = 0.12;
  let best = { snapped: coords[0], distance: Infinity, segmentIndex: 0, along: 0 };
  let bestScore = Infinity;
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1];
    const b = coords[i];
    const [ax, ay] = toXY(a);
    const [bx, by] = toXY(b);
    const dx = bx - ax;
    const dy = by - ay;
    const segLenSq = dx * dx + dy * dy;
    let t = 0;
    if (segLenSq > 0) {
      t = -(ax * dx + ay * dy) / segLenSq;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
    }
    const sx = ax + t * dx;
    const sy = ay + t * dy;
    const dist = Math.sqrt(sx * sx + sy * sy);
    const segLen = cums[i] - cums[i - 1];
    const along = cums[i - 1] + segLen * t;
    // Pick by cross-distance, but when a hint is given, bias toward the
    // part of the route the driver was last on.
    const score = hintAlong == null
      ? dist
      : dist + ALONG_PENALTY * Math.abs(along - hintAlong);
    if (score < bestScore) {
      bestScore = score;
      best = {
        snapped: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t],
        distance: dist,
        segmentIndex: i - 1,
        along,
      };
    }
  }
  return best;
}

/** Bearing in degrees (0=N, 90=E) from a→b. */
export function bearing(a: LngLat, b: LngLat): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const φ1 = toRad(a[1]);
  const φ2 = toRad(b[1]);
  const Δλ = toRad(b[0] - a[0]);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}
